import { theme } from 'lib/theme';
import React from 'react';
import {
  LayoutChangeEvent,
  Platform,
  ScrollViewProps,
  StyleProp,
  StyleSheet,
  View,
  ViewProps,
  ViewStyle,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { BlurView } from 'expo-blur';
import {
  KeyboardAwareScrollViewProps,
  KeyboardAwareScrollViewRef,
} from 'react-native-keyboard-controller';
import { NAVIGATION_HEADER_HEIGHT } from './PageHeader';
import MaskedView from '@react-native-masked-view/masked-view';
import { LinearGradient } from 'expo-linear-gradient';
import { useIsInModal } from 'hooks/isInModal';
import { SizingProvider, useSizing } from 'hooks/useSizing';
import { KeyboardAware } from './KeyboardAware';

export const PAGE_SHOW_FLOATING_HEADER = Platform.OS === 'ios';

type Props = {
  header?: React.ReactNode;
  underHeader?: React.ReactNode;
  additionalHeaderHeight?: number;
  backgroundColor?: string;
  headerBackgroundColor?: string;
  pageStyle?: StyleProp<ViewStyle>;
  children?: React.ReactNode | React.ReactNode[];
  showFloatingHeader?: boolean;
  showFloatingHeaderFade?: boolean;
  keyboardAvoiding?: boolean;

  /**
   * Wrap children in a ScrollView and apply safe areas including header avoiding
   */
  scrollView?: boolean;
  scrollViewContentContainerStyle?: ScrollViewProps['contentContainerStyle'];
  scrollEnabled?: boolean;

  onLayout?: ViewProps['onLayout'];
  onScroll?: KeyboardAwareScrollViewProps['onScroll'];
  scrollViewRef?: React.RefObject<KeyboardAwareScrollViewRef | null>;
  onHeaderChangeHeight?: (height: number) => void;

  /**
   * Defaults to: scrollView=theme.padding*2, nonScrollView=0
   */
  paddingTop?: number;

  /**
   * Defaults to: scrollView=theme.padding*2, nonScrollView=0
   */
  paddingBottom?: number;

  /**
   * Avoid bottom device safe areas
   * Defaults to: scrollView=true, nonScrollView=false
   */
  withSafeAreaBottom?: boolean;
  /**
   * Avoid header and device safe areas
   * Defaults to: scrollView=true, nonScrollView=false
   */
  withSafeAreaTop?: boolean;

  withSizingProvider?: boolean;
};

export const Page = ({
  pageStyle,
  backgroundColor,
  headerBackgroundColor,
  children,
  header,
  underHeader,
  showFloatingHeader = true,
  showFloatingHeaderFade = true,
  keyboardAvoiding = true,
  scrollView: withScrollView = false,
  scrollViewContentContainerStyle,
  scrollEnabled = true,
  paddingTop: incomingPaddingTop,
  paddingBottom: incomingPaddingBottom,
  withSafeAreaBottom: incomingWithSafeAreaBottom,
  withSafeAreaTop: incomingWithSafeAreaTop,
  onLayout,
  onScroll,
  scrollViewRef,
  additionalHeaderHeight = 0,
  onHeaderChangeHeight,
  withSizingProvider = true,
}: React.PropsWithChildren<Props>): React.ReactElement => {
  const isInModal = useIsInModal();
  const parentSizing = useSizing();

  const {
    left,
    right,
    top: safeAreaTop,
    bottom: safeAreaBottom,
  } = useSafeAreaInsets();

  const withSafeAreaBottom =
    incomingWithSafeAreaBottom !== undefined
      ? incomingWithSafeAreaBottom
      : !!withScrollView;
  const withSafeAreaTop =
    incomingWithSafeAreaTop !== undefined
      ? incomingWithSafeAreaTop
      : !!withScrollView;

  const paddingTop =
    incomingPaddingTop !== undefined
      ? incomingPaddingTop
      : withScrollView
        ? theme.padding * 2
        : 0;
  const paddingBottom =
    incomingPaddingBottom !== undefined
      ? incomingPaddingBottom
      : withScrollView
        ? theme.padding * 2
        : 0;

  const finalShowFloatingHeader =
    showFloatingHeader && PAGE_SHOW_FLOATING_HEADER;
  const top = !isInModal ? safeAreaTop : 0;
  const isInMobileModal = isInModal && Platform.OS === 'ios' && !Platform.isPad;
  const bottom = !isInModal
    ? Math.max(safeAreaBottom, 98)
    : isInMobileModal
      ? 0
      : 0;

  const [headerHeight, setHeaderHeight] = React.useState(
    finalShowFloatingHeader
      ? NAVIGATION_HEADER_HEIGHT + top + (additionalHeaderHeight || 0)
      : 0,
  );
  const handleHeaderLayout = React.useCallback(
    (event: LayoutChangeEvent) => {
      const newHeaderHeight = finalShowFloatingHeader
        ? Math.round(event.nativeEvent.layout.height)
        : 0;
      setHeaderHeight(newHeaderHeight);
      onHeaderChangeHeight?.(newHeaderHeight);
    },
    [finalShowFloatingHeader, onHeaderChangeHeight],
  );

  return (
    <WithFloatingHeader
      header={header}
      underHeader={underHeader}
      // headerPaddingTop={top}
      headerPaddingTop={0}
      headerBackgroundColor={headerBackgroundColor}
      headerHeight={headerHeight}
      onHeaderLayout={handleHeaderLayout}
      pageStyle={pageStyle}
      showFloatingHeader={finalShowFloatingHeader}
      showFloatingHeaderFade={showFloatingHeaderFade}
      backgroundColor={backgroundColor}
    >
      <SizingProvider
        headerHeight={
          withSizingProvider
            ? headerHeight + additionalHeaderHeight
            : parentSizing.headerHeight
        }
        safeAreaTop={withSizingProvider ? top : parentSizing.safeAreaTop}
        safeAreaBottom={
          withSizingProvider ? bottom : parentSizing.safeAreaBottom
        }
        safeAreaLeft={withSizingProvider ? left : parentSizing.safeAreaLeft}
        safeAreaRight={withSizingProvider ? right : parentSizing.safeAreaRight}
      >
        <KeyboardAware
          onLayout={onLayout}
          keyboardAvoiding={keyboardAvoiding}
          backgroundColor={backgroundColor}
          contentPaddingBottom={paddingBottom}
          contentPaddingTop={paddingTop}
          withSafeAreaTop={withSafeAreaTop}
          withSafeAreaBottom={withSafeAreaBottom}
          // Scroll view specific props
          scrollView={withScrollView}
          scrollViewRef={scrollViewRef}
          scrollEnabled={scrollEnabled}
          scrollViewContentContainerStyle={scrollViewContentContainerStyle}
          scrollIndicatorInsets={
            headerHeight ? { top: headerHeight - top } : undefined
          }
          onScroll={onScroll}
        >
          {children}
        </KeyboardAware>
      </SizingProvider>
    </WithFloatingHeader>
  );
};

const WithFloatingHeader = ({
  children,
  header,
  underHeader,
  headerHeight,
  headerPaddingTop,
  showFloatingHeader,
  showFloatingHeaderFade = true,
  pageStyle,
  backgroundColor,
  onHeaderLayout,
  headerBackgroundColor,
}: {
  children: React.ReactNode;
  header?: React.ReactNode;
  underHeader?: React.ReactNode;
  headerHeight: number;
  headerPaddingTop: number;
  showFloatingHeader: boolean;
  showFloatingHeaderFade: boolean;
  pageStyle?: ViewProps['style'];
  backgroundColor: string | undefined;
  onHeaderLayout: (event: LayoutChangeEvent) => void;
  headerBackgroundColor?: string;
}) => {
  const isInModal = useIsInModal();
  const { top: safeAreaTopRaw } = useSafeAreaInsets();

  // Floating header
  if (showFloatingHeader && header) {
    const safeAreaTop = isInModal ? 0 : safeAreaTopRaw;
    return (
      <View
        style={[
          {
            flex: 1,
            backgroundColor: backgroundColor || theme.colors.background,
          },
          pageStyle,
        ]}
      >
        {showFloatingHeaderFade ? (
          <MaskedView
            maskElement={
              <View style={StyleSheet.absoluteFill}>
                <View
                  style={{
                    height: headerHeight - 48,
                    backgroundColor: 'rgba(0,0,0,0.1)',
                  }}
                ></View>
                <LinearGradient
                  colors={['rgba(0,0,0,0.1)', 'black']}
                  style={{ height: 48 }}
                ></LinearGradient>
                <View style={{ flex: 1, backgroundColor: 'black' }}></View>
              </View>
            }
            style={[StyleSheet.absoluteFill]}
          >
            {children}
          </MaskedView>
        ) : (
          children
        )}
        {showFloatingHeaderFade && (
          <MaskedView
            maskElement={
              <>
                <View
                  style={{ height: 20 + safeAreaTop, backgroundColor: 'white' }}
                ></View>
                <LinearGradient
                  colors={['white', 'transparent']}
                  style={{ flex: 1, pointerEvents: 'none' }}
                ></LinearGradient>
              </>
            }
            style={{
              left: 0,
              position: 'absolute',
              top: 0,
              right: 0,
              zIndex: 1,
              height: 80 + safeAreaTop,
              pointerEvents: 'none',
              // backgroundColor: 'blue',
            }}
          >
            <BlurView
              intensity={10}
              tint="light"
              style={[{ flex: 1, pointerEvents: 'none' }]}
            ></BlurView>
          </MaskedView>
        )}
        <View
          style={{
            left: 0,
            position: 'absolute',
            top: 0,
            right: 0,
            zIndex: 1,
          }}
          onLayout={onHeaderLayout}
        >
          {header}
          {underHeader}
        </View>
      </View>
    );
  }

  // Non-floating header
  return (
    <View
      style={[
        {
          flex: 1,
          backgroundColor: backgroundColor || theme.colors.background,
        },
        pageStyle,
      ]}
    >
      <View
        style={{
          zIndex: 1,
          backgroundColor: headerBackgroundColor ?? theme.colors.background,
          borderBottomWidth: 1,
          borderBottomColor:
            headerBackgroundColor === 'transparent' ? 'transparent' : '#ededed',
          paddingTop: headerPaddingTop,
        }}
        onLayout={onHeaderLayout}
      >
        {header}
        {underHeader}
      </View>
      {children}
    </View>
  );
};
